// A table artifact's per-viewer view: one sort, column filters and a search
// that change what the reader sees, never the stored table. Rows keep their
// identity as STORED indices, so cell citations, the cited-cell marks and the
// cell menu keep working on a narrowed, reordered view. The sort and filters
// are remembered in the browser per artifact and per sheet name, as column
// widths are; the search is not.
import { compareScalars } from "../FpPages/RMS/Components/scalars";
import type { Scalar } from "../FpPages/RMS/Components/types";
import { formatDayDisplay } from "../FpPages/RMS/Search/Filter/typedDay";
import {
  displayCell,
  isRecordCell,
  recordCellLabel,
  type TableCell,
  type TableColumn,
  type TableSheet,
} from "./tableArtifact";

export type TableSort = { key: string; direction: "asc" | "desc" };

/** One column's filter. `values` holds display values (what the reader
 *  sees: "Yes", "$1,000.00"); `blank` keeps the blank cells. Days are ISO
 *  `YYYY-MM-DD` strings, compared as strings. */
export type ColumnFilter =
  | { kind: "values"; values: string[]; blank: boolean }
  | { kind: "contains"; text: string }
  | { kind: "numberRange"; min: number | null; max: number | null }
  | { kind: "dayRange"; from: string | null; to: string | null };

export type TableView = {
  sort: TableSort | null;
  filters: Record<string, ColumnFilter>;
  search: string;
};

export const EMPTY_VIEW: TableView = { sort: null, filters: {}, search: "" };

/** A header click's next sort: ascending, then descending, then the
 *  stored order. Another column starts again at ascending. */
export const nextSort = (
  current: TableSort | null,
  key: string
): TableSort | null => {
  if (current?.key !== key) return { key, direction: "asc" };
  return current.direction === "asc" ? { key, direction: "desc" } : null;
};

export type ViewStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const isBlank = (value: TableCell | undefined): boolean =>
  value === null || value === undefined || value === "";

/** Text without the commas that group digits ("1,234.5" → "1234.5"). */
const withoutDigitGroups = (text: string): string =>
  text.replace(/(\d),(?=\d{3}(?!\d))/g, "$1");

/** A cell's number: a number, or a numeric string read by its value. */
export const cellNumber = (value: TableCell | undefined): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const text = withoutDigitGroups(value.trim());
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(text)) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

/** A cell as the RMS comparator's scalar, typed by its column. */
const cellScalar = (
  value: TableCell | undefined,
  column: TableColumn
): Scalar => {
  if (isBlank(value) || value === undefined)
    return { kind: "text", value: null };
  if (isRecordCell(value))
    return { kind: "text", value: recordCellLabel(value) };
  switch (column.type) {
    case "number": {
      const number = cellNumber(value);
      return number === null
        ? { kind: "text", value: String(value) }
        : { kind: "number", value: number, precision: null, unit: null };
    }
    case "boolean":
      if (value === true || value === "true")
        return { kind: "boolean", value: true };
      if (value === false || value === "false")
        return { kind: "boolean", value: false };
      return { kind: "text", value: String(value) };
    case "date":
    case "text":
    case "link":
    case "record":
      return { kind: "text", value: String(value) };
    default: {
      const unhandled: never = column.type;
      throw new Error(`Unsupported column type: ${String(unhandled)}`);
    }
  }
};

/** Two cells of one column in ascending order, blanks last. */
export const compareCells = (
  left: TableCell | undefined,
  right: TableCell | undefined,
  column: TableColumn
): number =>
  // This view's cells compare as text, numbers or booleans, never as a day or an instant, so
  // the zone (which only places an instant against a day) is never read.
  compareScalars(cellScalar(left, column), cellScalar(right, column), "UTC");

/** What the reader sees in a cell, or null for a blank. */
const cellText = (
  value: TableCell | undefined,
  column: TableColumn
): string | null =>
  isBlank(value) || value === undefined
    ? null
    : displayCell(value, column.type, column.format);

const passes = (
  value: TableCell | undefined,
  column: TableColumn,
  filter: ColumnFilter
): boolean => {
  switch (filter.kind) {
    case "values": {
      const text = cellText(value, column);
      return text === null ? filter.blank : filter.values.includes(text);
    }
    case "contains": {
      const text = cellText(value, column);
      return (
        text !== null &&
        text.toLowerCase().includes(filter.text.trim().toLowerCase())
      );
    }
    case "numberRange": {
      const number = cellNumber(value);
      if (number === null) return false;
      if (filter.min !== null && number < filter.min) return false;
      if (filter.max !== null && number > filter.max) return false;
      return true;
    }
    case "dayRange": {
      // Only a calendar day compares as one; anything else in a date
      // column matches no range.
      if (!isDay(value)) return false;
      if (filter.from !== null && value < filter.from) return false;
      if (filter.to !== null && value > filter.to) return false;
      return true;
    }
    default: {
      const unhandled: never = filter;
      throw new Error(`Unsupported filter: ${JSON.stringify(unhandled)}`);
    }
  }
};

/** The sheet's STORED row indices that the view keeps, in display order. */
export const viewRows = (sheet: TableSheet, view: TableView): number[] => {
  const byKey = new Map(sheet.columns.map((c) => [c.key, c]));
  const filters = Object.entries(view.filters).flatMap(([key, filter]) => {
    const column = byKey.get(key);
    return column ? [{ column, filter }] : [];
  });
  const search = view.search.trim().toLowerCase();
  const kept: number[] = [];
  sheet.rows.forEach((row, index) => {
    if (
      !filters.every(({ column, filter }) =>
        passes(row[column.key], column, filter)
      )
    )
      return;
    if (
      search &&
      !sheet.columns.some((column) =>
        (cellText(row[column.key], column) ?? "").toLowerCase().includes(search)
      )
    )
      return;
    kept.push(index);
  });
  const sortColumn = view.sort ? byKey.get(view.sort.key) : undefined;
  if (!view.sort || !sortColumn) return kept;
  const sign = view.sort.direction === "asc" ? 1 : -1;
  const key = sortColumn.key;
  // Array sort is stable: equal values keep their stored order. A blank
  // stays last in both directions, as the RMS widget table's sort does.
  return [...kept].sort((a, b) => {
    const left = sheet.rows[a][key];
    const right = sheet.rows[b][key];
    const comparison = compareCells(left, right, sortColumn);
    return isBlank(left) || isBlank(right) ? comparison : comparison * sign;
  });
};

/** A column's distinct display values in the column's sort order, and
 *  whether it holds blanks. */
export const distinctValues = (
  sheet: TableSheet,
  column: TableColumn
): { values: string[]; blank: boolean } => {
  const first = new Map<string, TableCell>();
  let blank = false;
  for (const row of sheet.rows) {
    const value = row[column.key];
    const text = cellText(value, column);
    if (text === null) blank = true;
    else if (!first.has(text)) first.set(text, value ?? null);
  }
  const values = [...first.entries()]
    .sort(([, a], [, b]) => compareCells(a, b, column))
    .map(([text]) => text);
  return { values, blank };
};

const filterFits = (filter: ColumnFilter, column: TableColumn): boolean => {
  switch (filter.kind) {
    case "values":
      return filter.values.length > 0 || filter.blank;
    case "contains":
      return (
        filter.text.trim() !== "" &&
        column.type !== "number" &&
        column.type !== "date"
      );
    case "numberRange":
      return (
        column.type === "number" && (filter.min !== null || filter.max !== null)
      );
    case "dayRange":
      return (
        column.type === "date" && (filter.from !== null || filter.to !== null)
      );
    default: {
      const unhandled: never = filter;
      throw new Error(`Unsupported filter: ${JSON.stringify(unhandled)}`);
    }
  }
};

/** The view with what no longer applies dropped: a sort or filter on a
 *  column the sheet lacks, a filter that suits another column type, and an
 *  empty filter (a values filter with nothing checked hides nothing). */
export const normalizeView = (
  view: TableView,
  columns: TableColumn[]
): TableView => {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const filters: Record<string, ColumnFilter> = {};
  for (const [key, filter] of Object.entries(view.filters)) {
    const column = byKey.get(key);
    if (column && filterFits(filter, column)) filters[key] = filter;
  }
  return {
    sort: view.sort && byKey.has(view.sort.key) ? view.sort : null,
    filters,
    search: view.search,
  };
};

export const BLANK_LABEL = "(Blank)";

const NUMBER = new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 });

/** A filter as its chip reads: the column, then the criterion. */
export const filterChipText = (
  column: TableColumn,
  filter: ColumnFilter
): string => {
  switch (filter.kind) {
    case "values": {
      const picked = [...filter.values, ...(filter.blank ? [BLANK_LABEL] : [])];
      return picked.length > 3
        ? `${column.label}: ${picked.length} values`
        : `${column.label}: ${picked.join(", ")}`;
    }
    case "contains":
      return `${column.label} contains ${filter.text.trim()}`;
    case "numberRange": {
      const { min, max } = filter;
      if (min !== null && max !== null)
        return `${column.label}: ${NUMBER.format(min)} to ${NUMBER.format(max)}`;
      if (min !== null) return `${column.label} ≥ ${NUMBER.format(min)}`;
      if (max !== null) return `${column.label} ≤ ${NUMBER.format(max)}`;
      // normalizeView drops a range with no bound, so none reaches a chip.
      throw new Error(`A range on ${column.key} has no bound`);
    }
    case "dayRange": {
      const { from, to } = filter;
      if (from !== null && to !== null)
        return `${column.label}: ${formatDayDisplay(from)} to ${formatDayDisplay(to)}`;
      if (from !== null)
        return `${column.label} from ${formatDayDisplay(from)}`;
      if (to !== null) return `${column.label} until ${formatDayDisplay(to)}`;
      throw new Error(`A day range on ${column.key} has no bound`);
    }
    default: {
      const unhandled: never = filter;
      throw new Error(`Unsupported filter: ${JSON.stringify(unhandled)}`);
    }
  }
};

const viewStorageKey = (
  artifactId: string | undefined,
  sheetName: string
): string | null =>
  artifactId
    ? `fp.tableArtifact.view.${artifactId}.${encodeURIComponent(sheetName)}`
    : null;

const browserStorage = (storage: ViewStorage | undefined): ViewStorage =>
  storage ?? window.localStorage;

const isDay = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
const isNumberOrNull = (value: unknown): value is number | null =>
  value === null || (typeof value === "number" && Number.isFinite(value));

const parseFilter = (raw: unknown): ColumnFilter | null => {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  switch (f.kind) {
    case "values":
      return Array.isArray(f.values) &&
        f.values.every((v) => typeof v === "string")
        ? {
            kind: "values",
            values: f.values as string[],
            blank: f.blank === true,
          }
        : null;
    case "contains":
      return typeof f.text === "string"
        ? { kind: "contains", text: f.text }
        : null;
    case "numberRange":
      return isNumberOrNull(f.min) && isNumberOrNull(f.max)
        ? { kind: "numberRange", min: f.min, max: f.max }
        : null;
    case "dayRange":
      return (f.from === null || isDay(f.from)) &&
        (f.to === null || isDay(f.to))
        ? { kind: "dayRange", from: f.from, to: f.to }
        : null;
    default:
      return null;
  }
};

/** The remembered sort and filters for one sheet of one artifact, with
 *  anything the sheet no longer has dropped. The search always starts
 *  empty. Unreadable storage reads as no view. */
export const readStoredView = (
  artifactId: string | undefined,
  sheetName: string,
  columns: TableColumn[],
  storage?: ViewStorage
): TableView => {
  const key = viewStorageKey(artifactId, sheetName);
  if (!key) return EMPTY_VIEW;
  try {
    const raw = browserStorage(storage).getItem(key);
    if (!raw) return EMPTY_VIEW;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY_VIEW;
    const stored = parsed as { sort?: unknown; filters?: unknown };
    const rawSort = stored.sort as {
      key?: unknown;
      direction?: unknown;
    } | null;
    const sort: TableSort | null =
      rawSort &&
      typeof rawSort.key === "string" &&
      (rawSort.direction === "asc" || rawSort.direction === "desc")
        ? { key: rawSort.key, direction: rawSort.direction }
        : null;
    const filters: Record<string, ColumnFilter> = {};
    if (stored.filters && typeof stored.filters === "object") {
      for (const [k, v] of Object.entries(
        stored.filters as Record<string, unknown>
      )) {
        const filter = parseFilter(v);
        if (filter) filters[k] = filter;
      }
    }
    return normalizeView({ sort, filters, search: "" }, columns);
  } catch {
    return EMPTY_VIEW;
  }
};

/** Remember the view's sort and filters (never its search). */
export const writeStoredView = (
  artifactId: string | undefined,
  sheetName: string,
  view: TableView,
  storage?: ViewStorage
): void => {
  const key = viewStorageKey(artifactId, sheetName);
  if (!key) return;
  try {
    const store = browserStorage(storage);
    if (view.sort === null && Object.keys(view.filters).length === 0) {
      store.removeItem(key);
      return;
    }
    store.setItem(
      key,
      JSON.stringify({ sort: view.sort, filters: view.filters })
    );
  } catch {
    // Storage can be unavailable (private mode); the view still applies
    // for this pane's lifetime.
  }
};
